import React, { useEffect, useState } from 'react';
import Navbar from './Navbar.js';
import Footer from './Footer.js';
import '../styles/FamousFoods.css';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function FamousFoods() {
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [displayRestaurants, setDisplayRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inputPlace, setInputPlace] = useState('');
  const [city, setCity] = useState('');

  const { loggedIn, fetchWithAuth, loading: authLoading } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const RESTAURANTS_PER_BATCH = 4;

  // Convert place name to coordinates
  const getCoordsFromPlace = async (place) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(place)}&limit=1`
      );
      const data = await res.json();
      if (!data || data.length === 0) return null;
      return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
    } catch {
      return null;
    }
  };

  // Get city from coordinates
  const getCityFromCoords = async (lat, lon) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
      );
      const data = await res.json();
      return (
        data.address.city ||
        data.address.town ||
        data.address.village ||
        data.address.county ||
        ''
      );
    } catch {
      return '';
    }
  };

  // Fetch restaurants by coordinates
  const fetchRestaurantsByCoords = async (coords) => {
    if (!coords) {
      setError('Invalid place');
      setLoading(false);
      return;
    }
    try {
      const cityName = inputPlace || (await getCityFromCoords(coords.lat, coords.lon));
      setCity(cityName);

      const response = await fetchWithAuth(
        `${API_URL}/nearby-restaurants?lat=${coords.lat}&lon=${coords.lon}&radius=10000`
      );
      if (!response.ok) throw new Error('Failed to fetch restaurants');

      const data = await response.json();
      const nearbyRestaurants = Array.isArray(data.restaurants) ? data.restaurants : [];

      setAllRestaurants(nearbyRestaurants);
      setDisplayRestaurants(nearbyRestaurants.slice(0, RESTAURANTS_PER_BATCH));
      setError('');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fetch restaurants');
    } finally {
      setLoading(false);
    }
  };

  // Handle user place submit
  const handlePlaceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const coords = await getCoordsFromPlace(inputPlace);
    fetchRestaurantsByCoords(coords);
  };

  // Infinite scroll
  const loadNextRestaurants = () => {
    const currentCount = displayRestaurants.length;
    const nextBatch = allRestaurants.slice(currentCount, currentCount + RESTAURANTS_PER_BATCH);
    setDisplayRestaurants([...displayRestaurants, ...nextBatch]);
  };

  const handleScroll = () => {
    if (
      window.innerHeight + document.documentElement.scrollTop + 100 >=
      document.documentElement.scrollHeight
    ) {
      loadNextRestaurants();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [displayRestaurants, allRestaurants]);

  // Initial load: geolocation if no place entered
  useEffect(() => {
    if (!authLoading && !loggedIn) {
      navigate('/login');
      return;
    }

    if (!inputPlace) {
      const fetchRestaurants = async () => {
        try {
          let location = null;
          const cachedLocation = sessionStorage.getItem('userLocation');
          const cachedTime = sessionStorage.getItem('locationTimestamp');

          if (cachedLocation && cachedTime && Date.now() - cachedTime < 5 * 60 * 1000) {
            location = JSON.parse(cachedLocation);
          } else if (navigator.geolocation) {
            location = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(
                (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
                (err) => reject(err)
              );
            });
            sessionStorage.setItem('userLocation', JSON.stringify(location));
            sessionStorage.setItem('locationTimestamp', Date.now());
          } else {
            throw new Error('Geolocation is not supported by this browser.');
          }

          fetchRestaurantsByCoords(location);
        } catch (err) {
          console.error(err);
          setError(err.message || 'Failed to fetch restaurants.');
          setLoading(false);
        }
      };

      fetchRestaurants();
    }
  }, [loggedIn, API_URL, fetchWithAuth, navigate, inputPlace]);

  return (
    <div className="famous-foods-container">
      <Navbar />
      <div className="famous-foods-content">
        <h2>Famous Restaurants Near You ❤️</h2>

        {/* Input for user place */}
        <form className="restaurant-search-form" onSubmit={handlePlaceSubmit}>
          <input
            type="text"
            value={inputPlace}
            placeholder="Enter city or place"
            onChange={(e) => setInputPlace(e.target.value)}
            className="restaurant-input-search"
          />
          <button type="submit" className="restaurant-button-search">
            Search
          </button>
        </form>

        {loading && (
          <div className="loading-destinations">
            <div className="loading-spinner"></div>
            <p>Finding famous restaurants for you...</p>
          </div>
        )}

        {error && <p className="error">{error}</p>}
        {!loading && !error && displayRestaurants.length === 0 && <p>No restaurants found nearby.</p>}

        <div className="food-cards">
          {displayRestaurants.map((restaurant, index) => (
            <div className="food-card" key={index}>
              {restaurant.image && (
                <img src={restaurant.image} alt={restaurant.name} className="food-image" />
              )}
              <h3>{restaurant.name}</h3>
              <p><strong>Address:</strong> {restaurant.address}</p>
              <p><strong>Rating:</strong> {restaurant.rating !== 'N/A' ? restaurant.rating : 1}/3⭐</p>
              {restaurant.mustTry && <p><strong>Must Try:</strong> {restaurant.mustTry}</p>}
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
